import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Import Axios instance
import { PaginationInfo } from "../../types/api";

interface SecurityDeposit {
  id?: number;
  date: string;
  depositAmount: string | number;
  retentionMoney: string | number;
  incomeTax: string | number;
  gstTds: string | number;
  projectName: string;
  clientName: string;
  [key: string]: any;
}

interface SecurityDepositsContextType {
  deposits: SecurityDeposit[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchDeposits: (page?: number, perPage?: number) => Promise<void>;
  addDeposit: (depositDetails: any) => Promise<void>;
  editDeposit: (depositDetails: any) => Promise<void>;
  deleteDeposit: (id: number) => Promise<void>;
}

export const SecurityDepositsContext = createContext<SecurityDepositsContextType | undefined>(undefined);

interface SecurityDepositsProviderProps {
  children: ReactNode;
}

const SecurityDepositsProvider: React.FC<SecurityDepositsProviderProps> = ({ children }) => {
  const [deposits, setDeposits] = useState<SecurityDeposit[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all security deposits with pagination
  const fetchDeposits = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/security-deposits?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let depositsData: SecurityDeposit[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          depositsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          depositsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          depositsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        depositsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        depositsData = responseData;
      }
      
      setDeposits(depositsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch security deposits:", error);
      setDeposits([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new security deposit
  const addDeposit = async (depositDetails: any): Promise<void> => {
    try {
      const response = await api.post("/security-deposits", depositDetails);
      const newDeposit = response.data?.data || response.data;
      if (newDeposit) {
        setDeposits((prevDeposits) => [...prevDeposits, newDeposit]);
      }
    } catch (error) {
      console.error("Failed to add security deposit:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Edit an existing security deposit
  const editDeposit = async (depositDetails: any): Promise<void> => {
    try {
      const response = await api.put(`/security-deposits/${depositDetails.id}`, depositDetails);
      const updatedDeposit = response.data?.data || response.data;
      if (updatedDeposit) {
        setDeposits((prevDeposits) =>
          prevDeposits.map((item) =>
            item.id === depositDetails.id ? updatedDeposit : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to edit security deposit:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };

  // Delete a security deposit
  const deleteDeposit = async (id: number): Promise<void> => {
    try {
      await api.delete(`/security-deposits/${id}`);
      setDeposits((prevDeposits) => prevDeposits.filter((deposit) => deposit.id !== id));
    } catch (error) {
      console.error("Failed to delete deposit:", error);
      throw error; // Re-throw to allow component to handle error
    }
  };
  

  useEffect(() => {
    fetchDeposits(1, 15);
  }, [fetchDeposits]);

  return (
    <SecurityDepositsContext.Provider
      value={{ deposits, pagination, loading, fetchDeposits, addDeposit, editDeposit, deleteDeposit }}
    >
      {children}
    </SecurityDepositsContext.Provider>
  );
};

export default SecurityDepositsProvider;
